/**
 * Texte des Leitfadens der Planungsphase (docs/planungsphase-leitfaden-plan.md,
 * Abschnitt 2.4 „Sprache fuer Mitarbeiter"). Ohne React, damit Leiste,
 * Station, Hub-Kachel und Aufgabenliste dieselben Saetze lesen.
 *
 * Ton: Ueberschrift als Handlung in Sie-Form, ein Satz „Warum" mit Paragraf,
 * wo er traegt, und das Erledigt-Kriterium so, wie `leitfadenStand` es
 * tatsaechlich rechnet. Aendert sich dort eine Regel, muss `erledigtWenn`
 * mitwandern — sonst verspricht die Station etwas anderes, als die Leiste
 * zeigt.
 */

import type { LeitfadenAbschnitt, StationSchluessel, StationZustand } from './leitfadenTypen';

export interface StationText {
    /** Ueberschrift der Station als Handlung („Beschreiben Sie das Vorhaben"). */
    titel: string;
    /** Kurzform fuer Leiste, Weiter-Text und Kachel („Vorhaben beschreiben"). */
    kurzTitel: string;
    /** Ein Satz, warum die Station zaehlt. */
    warum: string;
    /** „Erledigt, sobald …" — das sichtbare Kriterium der Station. */
    erledigtWenn: string;
}

export const STATION_TEXTE: Record<StationSchluessel, StationText> = {
    beschreiben: {
        titel: 'Beschreiben Sie das Vorhaben',
        kurzTitel: 'Vorhaben beschreiben',
        warum: 'Die Baubeschreibung ist die Grundlage des Fragenkatalogs, und je genauer sie ist, desto weniger Fragen bleiben übrig.',
        erledigtWenn: 'Erledigt, sobald eine Baubeschreibung gespeichert ist.',
    },
    umfang: {
        titel: 'Klären Sie Umfang und Pflichten',
        kurzTitel: 'Umfang und Pflichten klären',
        warum: 'Bauzeit, Beschäftigte und Personentage entscheiden über die Vorankündigung (§ 2 Abs. 2 BaustellV), mehrere Arbeitgeber über SiGe-Plan und Koordinator (§ 2 Abs. 3, § 3 Abs. 1 BaustellV).',
        erledigtWenn: 'Erledigt, sobald die Angaben gespeichert sind und keine der drei Pflichten mehr ungeklärt ist. Fehlen noch Zahlen, dürfen Sie trotzdem weiter, die Station bleibt dann gelb vermerkt.',
    },
    merkmale: {
        titel: 'Setzen Sie die Merkmale des Vorhabens',
        kurzTitel: 'Merkmale setzen',
        warum: 'Jedes Merkmal schaltet Fragen des Katalogs frei, und nur ein ausdrückliches Nein blendet sie aus.',
        erledigtWenn: 'Erledigt, sobald jedes Merkmal auf Ja oder Nein steht. Bleiben Merkmale offen, gilt die Station mit gelbem Vermerk als erledigt, wenn Sie „Rest bleibt offen“ bestätigen oder der Katalog schon steht.',
    },
    katalog: {
        titel: 'Erzeugen Sie den Fragenkatalog',
        kurzTitel: 'Fragenkatalog erzeugen',
        warum: 'Aus den Merkmalen entsteht der Katalog für dieses Projekt, und ändern sich die Merkmale später, kommen Fragen hinzu.',
        erledigtWenn: 'Erledigt, sobald der Katalog steht. Nachziehbare oder gegenstandslos gewordene Fragen erscheinen als gelber Vermerk.',
    },
    versenden: {
        titel: 'Versenden Sie die Klärungslisten',
        kurzTitel: 'Klärungslisten versenden',
        warum: 'Jeder Adressat bekommt nur seine Fragen, und erst mit dem Versand beginnt die Frist zur Antwort.',
        erledigtWenn: 'Erledigt, sobald jede Frage gestellt ist.',
    },
    antworten: {
        titel: 'Arbeiten Sie die Antworten ein',
        kurzTitel: 'Antworten einarbeiten',
        warum: 'Antworten kommen über Wochen herein; hier tragen Sie sie ein und stellen abgelaufene Fristen fest.',
        erledigtWenn: 'Erledigt, sobald keine Frage mehr auf Antwort wartet. Den Fristablauf stellen Sie selbst fest, die App setzt ihn nie von allein.',
    },
    unterlagen: {
        titel: 'Fordern Sie die Unterlagen an',
        kurzTitel: 'Unterlagen anfordern',
        warum: 'Ohne Unterlagen zu Baugrund, Schadstoffen und Leitungen fehlt dem SiGe-Plan die Grundlage.',
        erledigtWenn: 'Erledigt, sobald jede erforderliche Unterlage vorliegt oder mit Begründung als nicht erforderlich vermerkt ist.',
    },
    bedenken: {
        titel: 'Zeigen Sie Bedenken an',
        kurzTitel: 'Bedenken anzeigen',
        warum: 'Bleibt eine haftungsrelevante Frage unbeantwortet, entlastet Sie nur eine versendete Bedenkenanzeige an den Bauherrn.',
        erledigtWenn: 'Erledigt, sobald jede haftungsrelevante Frage beantwortet, gegenstandslos oder durch eine versendete Bedenkenanzeige gedeckt ist.',
    },
    abschluss: {
        titel: 'Übergeben Sie an die Ausführung und schließen Sie ab',
        kurzTitel: 'Übergeben und abschließen',
        warum: 'Die Restrisikoliste geht in die Ausführungsphase über, und Vorankündigung, SiGe-Plan und Unterlage für spätere Arbeiten bauen darauf auf. Aus den gesammelten Angaben legt die App SiGe-Plan, Handbuch, Baustellenordnung und A+S-Plan als Entwurf an.',
        erledigtWenn: 'Erledigt, sobald Sie die Planungsphase abgeschlossen haben. Das geht, wenn jede haftungsrelevante Frage geklärt oder gedeckt ist, und danach lässt sie sich wieder öffnen. Die Folgeunterlagen wählen Sie beim Abschluss oder legen sie danach an; für die Station zählen sie nicht.',
    },
};

export const ABSCHNITT_TITEL: Record<LeitfadenAbschnitt, string> = {
    A: 'Vorhaben',
    B: 'Fragen',
    C: 'Nachweise',
    D: 'Abschluss',
};

export const ZUSTAND_LABEL: Record<StationZustand, string> = {
    erledigt: 'erledigt',
    aktuell: 'jetzt dran',
    offen: 'offen',
    wartet: 'wartet',
    handlungsbedarf: 'Handlungsbedarf',
    nicht_erforderlich: 'derzeit nicht nötig',
    gesperrt: 'noch gesperrt',
};

/** Hilfekaesten zu Fachbegriffen. Die Schluessel stehen im Browser-Merker
 *  (`blendeHilfeAus`) — nie umbenennen, nur ergaenzen. */
export type HilfeSchluessel =
    | 'merkmal_offen'
    | 'nachziehen'
    | 'gegenstandslos'
    | 'stellen'
    | 'feststellen'
    | 'deckt'
    | 'fassung'
    | 'restrisiko';

export const HILFE: Record<HilfeSchluessel, { titel: string; text: string }> = {
    merkmal_offen: {
        titel: '„offen“ ist kein Fehler',
        text: 'Offen heißt: wir wissen es noch nicht. Die zugehörigen Fragen bleiben im Katalog, nur ein ausdrückliches Nein blendet sie aus.',
    },
    nachziehen: {
        titel: 'Nachziehen ergänzt nur',
        text: 'Nachziehen fügt Fragen hinzu, die zu geänderten Merkmalen oder einer späteren Leistungsphase passen. Vorhandene Fragen bleiben unverändert, entfernt wird nie etwas.',
    },
    gegenstandslos: {
        titel: 'Gegenstandslos ist eine Entscheidung',
        text: 'Eine gegenstandslose Frage trifft auf dieses Projekt nicht zu. Das ist eine dokumentierte Entscheidung, und die Begründung steht in der internen Bewertung.',
    },
    stellen: {
        titel: 'Versenden stempelt',
        text: 'Jede Frage bekommt beim ersten Stellen ihr Stelldatum und eine Frist: A-Fragen 14 Tage, B- und C-Fragen 21 Tage. Das erste Stelldatum bleibt immer stehen, es zählt im Haftungsfall.',
    },
    feststellen: {
        titel: 'Fristablauf feststellen',
        text: '„Nicht beantwortet“ ist Ihre Feststellung nach Ablauf der Frist. Die App setzt diesen Status nie von allein.',
    },
    deckt: {
        titel: 'Nur eine versendete Bedenkenanzeige deckt',
        text: 'Ein Entwurf deckt nicht, ein bloßer Hinweis auch nicht. Adressat muss der Bauherr sein, denn eine Anzeige nur an den Architekten enthaftet regelmäßig nicht.',
    },
    fassung: {
        titel: 'Fassungsrückfrage',
        text: 'Ändern sich Angaben, auf denen eine freigegebene Unterlage beruht, fragt die App nach. Sie legen eine neue Fassung an, vermerken die Änderung mit Begründung als nicht erheblich oder entscheiden später.',
    },
    restrisiko: {
        titel: 'Restrisikoliste',
        text: 'Die Restrisikoliste hält fest, was beim Abschluss noch offen ist. Sie geht mit der Übergabe in die Ausführungsphase über.',
    },
};
